import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonIcon,
  OutlineCard,
  Table
} from '@morressier/alembic';
import { IAuthorWithContribution } from '@publishing-engine/domain/models';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import RinggoldBadge from 'src/components/RinggoldBadge';
import { OrcidIDIcon } from 'src/components/User/RenderSectionDetails';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { ContributionsBadge } from 'src/components/Submission/ContributionsBadge';
import { useDuplicateEmails } from 'src/hooks/useDuplicateEmails';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';

interface Props {
  showAuthorEditModal: () => void;
  config: FormFieldAuthors;
}

const AuthorList: React.FC<Props> = ({ showAuthorEditModal, config }) => {
  const { getValues } = useFormContext<ISubmissionFormData>();
  const authors = getValues('metadata.authors');
  const submittingAuthor = getValues('submittingAuthor');
  const contactAuthor = getValues('contactAuthor');
  const correspondingAuthor = getValues('correspondingAuthor');

  const authorRoleMap: IAuthorRolesMap = {
    Submitting: submittingAuthor,
    ...(contactAuthor && {
      Contact: contactAuthor
    }),
    ...(correspondingAuthor && {
      Corresponding: correspondingAuthor
    })
  };

  const { orcidField, affiliationField, contributionsField } =
    config.params.validations;

  const { duplicateEmails } = useDuplicateEmails(authors);
  const handleEditModal = () => {
    showAuthorEditModal();
  };
  const renderAuthorTableRow = (
    author: IAuthorWithContribution,
    index: number
  ) => {
    const isDuplicateEmail =
      author.email && duplicateEmails.has(author.email.toLowerCase().trim());
    const hasName =
      [author.firstName, author.middleName, author.lastName].filter(Boolean)
        .length > 0;
    return (
      <Table.Row
        alignItems="start"
        _last={{ borderBottom: 'none' }}
        flexDirection={{ base: 'column', md: 'row' }}
        data-cy="author-row"
      >
        <Table.RowCell
          width={{ base: '100%', md: '80px' }}
          display={{ base: 'none', md: 'flex' }}
        >
          {index + 1}
        </Table.RowCell>
        <Table.RowCell
          flex={1}
          gap={1}
          display="flex"
          flexDirection="column"
          width={{ base: '100%', md: 'auto' }}
          data-cy="author-cell"
        >
          {hasName ? (
            <Text textStyle="actionLabel" data-cy="author-name">
              {[author.firstName, author.middleName, author.lastName]
                .filter(Boolean)
                .join(' ')}
            </Text>
          ) : (
            <Text
              color="actionSecondaryCautionContent"
              textStyle="actionLabel"
              data-cy="no-name-data"
            >
              No Name Data
            </Text>
          )}
          {author.email ? (
            <Text
              textStyle="textContext"
              noOfLines={1}
              overflow="hidden"
              textOverflow="ellipsis"
              data-cy="author-email"
              color={
                isDuplicateEmail ? 'actionSecondaryCautionContent' : undefined
              }
            >
              {author.email}
            </Text>
          ) : (
            <Text
              color="actionSecondaryCautionContent"
              textStyle="textContext"
              data-cy="no-email-data"
            >
              No email address provided
            </Text>
          )}

          {orcidField !== 'hidden' && (
            <>
              {author.orcid ? (
                <Flex alignItems="center" flexDirection="row" gap={1}>
                  <OrcidIDIcon value={author.orcid} />
                  <Text textStyle="textContext">{author.orcid}</Text>
                </Flex>
              ) : (
                <Text
                  color={
                    orcidField === 'mandatory'
                      ? 'actionSecondaryCautionContent'
                      : ''
                  }
                  textStyle="textContext"
                >
                  No ORCID provided
                </Text>
              )}
            </>
          )}

          <Flex gap={2} flexWrap="wrap">
            {Object.keys(authorRoleMap).map(role => {
              if (
                authorRoleMap[role as keyof IAuthorRolesMap] === author.email
              ) {
                return (
                  <Badge variant="attention" size="context">
                    {role} Author
                  </Badge>
                );
              }
            })}
          </Flex>
        </Table.RowCell>

        {affiliationField !== 'hidden' && (
          <Table.RowCell
            flex={1}
            gap={2}
            padding={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            width={{ base: '100%', md: 'auto' }}
            data-cy="affiliation-cell"
          >
            <Text
              textStyle="textContext"
              fontWeight="semibold"
              display={{ base: 'block', md: 'none' }}
            >
              Affiliation
            </Text>
            <Flex flexDirection="row" gap={2} alignItems="flex-start" width="100%">
              {author.ringgoldId && <RinggoldBadge data-cy="ringgold-badge" />}
              {author.affiliation ? (
                <Flex flexDirection="column">
                  <Text textStyle="textBody" data-cy="author-affiliation">
                    {author.affiliation}
                    {author.city && `, ${author.city}`}
                    {author.country && `, ${author.country}`}
                    {author.postalCode && `, ${author.postalCode}`}
                  </Text>
                  {!author.city && (
                    <Text
                      color={
                        affiliationField === 'mandatory'
                          ? 'actionSecondaryCautionContent'
                          : ''
                      }
                      textStyle="textContext"
                      data-cy="no-affiliation-city"
                    >
                      No City provided
                    </Text>
                  )}
                  {!author.country && (
                    <Text
                      color={
                        affiliationField === 'mandatory'
                          ? 'actionSecondaryCautionContent'
                          : ''
                      }
                      textStyle="textContext"
                      data-cy="no-affiliation-country"
                    >
                      No Country provided
                    </Text>
                  )}
                </Flex>
              ) : (
                <Text
                  color={
                    affiliationField === 'mandatory'
                      ? 'actionSecondaryCautionContent'
                      : ''
                  }
                  textStyle="textBody"
                  data-cy="no-affiliation-data"
                >
                  No Affiliation provided
                </Text>
              )}
            </Flex>
          </Table.RowCell>
        )}

        {contributionsField !== 'hidden' && (
          <Table.RowCell
            flex={1}
            gap={2}
            padding={2}
            display="flex"
            flexDirection="column"
            width={{ base: '100%', md: 'auto' }}
            data-cy="contributions-cell"
          >
            <Text
              textStyle="textContext"
              fontWeight="semibold"
              display={{ base: 'block', md: 'none' }}
            >
              CRediT Contributions
            </Text>
            <ContributionsBadge
              contributions={author.contributions}
              isMandatory={contributionsField === 'mandatory'}
            />
          </Table.RowCell>
        )}
      </Table.Row>
    );
  };

  return (
    <OutlineCard flexDirection="column" width="100%">
      <Flex flexDirection="column" padding={{ base: 3, md: 4 }} width="100%">
        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', md: 'center' }}
          width="100%"
          data-cy="author-list-header"
          gap={{ base: 2, md: 0 }}
        >
          <Flex gap={2} alignItems="center" wrap="wrap">
            <Text
              textStyle="headingGroup"
              data-cy="author-list-title"
              id="Author List"
            >
              Author List ({authors.length})
            </Text>
            <Tooltip
              hasArrow
              shouldWrapChildren
              placement="top"
              label="Add full details for each author including names, affiliations, ORCID iD and contribution data as required for peer review and publication"
              borderRadius={4}
              data-cy="contributions-info-tooltip"
            >
              <ButtonIcon
                size="small"
                ariaLabel="Back to workspace settings"
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                data-cy="contributions-info-icon"
              />
            </Tooltip>
          </Flex>

          <Button
            onClick={() => handleEditModal()}
            data-cy="edit-authors-button"
            width={{ base: '100%', sm: 'auto' }}
          >
            Edit Authors
          </Button>
        </Flex>

        <Box width="100%">
          <Table data-cy="author-table">
            <Table.HeaderRow
              display={{ base: 'none', md: 'flex' }}
              alignItems="center"
              data-cy="author-table-header"
            >
              <Table.HeaderCell width="80px">S.No.</Table.HeaderCell>
              <Table.HeaderCell flex={1} data-cy="author-header-cell">
                Author
              </Table.HeaderCell>

              {affiliationField !== 'hidden' && (
                <Table.HeaderCell flex={1} data-cy="affiliation-header-cell">
                  Affiliation
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label="Enter the organization name for the author including city, country and postal code"
                    borderRadius={4}
                    data-cy="contributions-info-tooltip"
                  >
                    <ButtonIcon
                      size="small"
                      ariaLabel="Back to workspace settings"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                      data-cy="contributions-info-icon"
                    />
                  </Tooltip>
                </Table.HeaderCell>
              )}

              {contributionsField !== 'hidden' && (
                <Table.HeaderCell
                  flex={1}
                  alignItems="center"
                  data-cy="contributions-header-cell"
                >
                  CRediT Contributions
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label="Contributor Role Taxonomy (CRediT) Contributions"
                    borderRadius={4}
                    data-cy="contributions-info-tooltip"
                  >
                    <ButtonIcon
                      size="small"
                      ariaLabel="Back to workspace settings"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                      data-cy="contributions-info-icon"
                    />
                  </Tooltip>
                </Table.HeaderCell>
              )}
            </Table.HeaderRow>

            <Table.Body data-cy="author-table-body">
              {authors.map((author, index) => (
                <React.Fragment key={index}>
                  {renderAuthorTableRow(author, index)}
                </React.Fragment>
              ))}
            </Table.Body>
          </Table>
        </Box>
      </Flex>
    </OutlineCard>
  );
};

export default AuthorList;
